<script>
import Emoji from "../widget/Emoji";

let formatTypes = {
  bold: "B",
  italic: "I",
  underline: "U",
  strikethrough: "S",
};

export default {
  props: {
    value: {
      type: String,
      default: "",
    },
    id: [Number, String],
    label: {
      type: String,
      default: "",
    },
    hint: {
      type: String,
      default: "",
    },
    placeholders: {
      type: Array,
      default: () => {
        return [];
      },
    },
    error: {
      type: Object,
      default: () => {
        return {};
      },
    },
    minHeight: {
      type: Number,
      default: 150,
    },
    maxHeight: {
      type: Number,
      default: 300,
    },
  },
  data() {
    return {
      domElement: null,
      editor: null,
      path: null,
      editorHasFocus: false,
      hasSelection: false,
      placeholderSelected: false,
      selectionRange: null,
      showLinker: false,
      showReferenceInsert: false,
      referenceHashtag: false,
      linkerUrl: "",
      setEmoji: false,
      isBold: false,
      isItalic: false,
      isUnderlined: false,
      isStrike: false,
      isLink: false,
      placeholder: null,
      contentLength: 0,
      placeholderSelection: {},
    };
  },
  mounted() {
    const Squire = require("squire-rte");
    this.domElement = this.$refs["squire-editor"].$el;
    this.editor = new Squire(this.domElement, {
      blockTag: "div",
    });

    this.editor.setHTML(this.value);
    this.editor.addEventListener("focus", this.focus);
    this.editor.addEventListener("blur", this.blur);
    this.editor.addEventListener("input", this.update);
    this.editor.addEventListener("cursor", this.cursorChange);
    this.editor.addEventListener("select", this.cursorChange);
    this.editor.addEventListener("pathChange", this.pathChange);
  },
  computed: {
    editorError: {
      get() {
        return (
          this.id &&
          this.error.errors &&
          this.error.errors.editor &&
          this.error.errors.editor[this.id]
        );
      },
      set(value) {
        this.error.errors.editor[this.id] = value;
      },
    },
  },
  methods: {
    focus() {
      this.reset();
      this.editorHasFocus = true;
    },
    blur() {
      this.reset();
      this.editorHasFocus = false;
    },
    update() {
      if (this.editorError) {
        this.editorError = null;
      }
      let content = this.editor.getHTML();
      this.$emit("input", content);
    },
    insert(text) {
      let range = this.editor.getSelection();
      this.editor.saveUndoState(range);
      this.editor.insertHTML(text);
    },
    makeHashtag() {
      if (this.placeholderSelected) {
        let content = this.editor.getHTML();

        content = content.replace(this.placeholderSelected, `@hashtag(${this.placeholderSelected})/`);
        this.editor.setHTML(content);
        this.placeholderSelected = false;
      }
    },
    cursorChange(event) {
      this.hasSelection = event.type === "select";
      if (this.showLinker && event.type === "cursor") {
        this.showLinker = false;
      } else if (event.type === "select" && event.range) {
        let href =
          event.range.startContainer.parentElement.attributes &&
          event.range.startContainer.parentElement.attributes.href;
        if (href) this.linkerUrl = href.value;
      }
      
      if ((event.type === "cursor" || event.type === "select") && event.range) {
        let str = (event.type === "select")
          ? event.range.startContainer.textContent.substring(event.range.startOffset, event.range.endOffset) 
          : event.range.startContainer.textContent;
        
        let matches = str.match(/{{\s*[\w.]+\s*}}/g);

        if (matches && matches.length === 1) {
          this.placeholderSelected = matches[0].replace(/[{}]/g, '');
        } else {
          this.placeholderSelected = false;
        }
      }
      this.state();
    },
    pathChange(event) {
      this.path = event.path || this.editor.getPath();
      this.state();
    },
    format(formatter) {
      if (formatter === false) {
        return this.removeFormatting();
      } else if (!formatTypes[formatter]) {
        return;
      }

      let hasFormat = this.editor.hasFormat(formatTypes[formatter]);
      hasFormat
        ? this.editor[
            `remove${formatter.charAt(0).toUpperCase() + formatter.slice(1)}`
          ]()
        : this.editor[formatter]();
    },
    removeFormatting() {
      if (!this.hasSelection) {
        let range = new Range();
        range.selectNodeContents(this.domElement);
        this.editor.setSelection(range);
        this.editor.removeAllFormatting();
        this.editor.moveCursorToEnd();
      } else {
        this.editor.removeAllFormatting();
      }
    },
    state() {
      this.isBold = this.editor.hasFormat("B");
      this.isItalic = this.editor.hasFormat("I");
      this.isUnderlined = this.editor.hasFormat("U");
      this.isStrike = this.editor.hasFormat("S");
      this.isLink = this.editor.hasFormat("A");
    },
    reset() {
      this.path = null;
      this.isBold = false;
      this.isItalic = false;
      this.isUnderlined = false;
      this.isStrike = false;
    },
    makeLink() {
      if (!this.linkerUrl || !this.linkerUrl.length) return;
      this.editor.makeLink(this.linkerUrl, { target: "_blank" });
      this.linkerUrl = null;
      this.showLinker = false;
    },
    emojiCallback(emoji) {
      this.insert(emoji.data);
    },
    insertPlaceholder(placeholder) {
      this.insert(placeholder);
    },
    insertReference(ht) {
      this.insert(`${ht ? '#':''}{{it.message.reference}}`);
      this.showReferenceInsert = false;
    },
  },
  watch: {
    id(now, before) {
      if (now !== before) this.editor.setHTML(this.value);
    },
    showLinker(show) {
      if (show && !this.isLink && this.linkerUrl) this.linkerUrl = null;
    },
  },
  components: {
    Emoji,
  },
};
</script>

<template>
  <section class="editor-wrapper mb-3">
    <emoji :show.sync="setEmoji" :emoji="emojiCallback"></emoji>
    <v-sheet :elevation="0" class="mb-2" v-if="label">
      <label
        class="v-label"
        :class="this.$vuetify.theme.dark ? 'theme--dark' : 'theme--light'"
        >{{ label }}</label
      >
    </v-sheet>
    <v-toolbar :elevantion="0" :dark="$vuetify.theme.dark">
      <v-btn icon @click="format('bold')" :outlined="isBold">
        <v-icon>mdi-format-bold</v-icon>
      </v-btn>
      <v-btn icon @click="format('italic')" :outlined="isItalic">
        <v-icon>mdi-format-italic</v-icon>
      </v-btn>
      <v-btn icon @click="format('underline')" :outlined="isUnderlined">
        <v-icon>mdi-format-underline</v-icon>
      </v-btn>
      <v-btn icon @click="format('strikethrough')" :outlined="isStrike">
        <v-icon>mdi-format-strikethrough</v-icon>
      </v-btn>
      <v-btn icon @click="format(false)">
        <v-icon>mdi-format-clear</v-icon>
      </v-btn>
      <v-btn icon @click="setEmoji = !setEmoji">
        <v-icon>mdi-sticker-emoji</v-icon>
      </v-btn>
      <v-btn icon @click="showReferenceInsert = !showReferenceInsert" title="Insert reference if action using this elaborator has it enabled.">
        <v-icon>mdi-numeric</v-icon>
      </v-btn>
      <v-btn icon @click="showLinker = !showLinker" v-if="hasSelection">
        <v-icon>mdi-link-plus</v-icon>
      </v-btn>
      <v-btn icon @click="editor.removeLink()" v-if="isLink">
        <v-icon>mdi-link-off</v-icon>
      </v-btn>
      <v-btn icon v-if="placeholderSelected" @click="makeHashtag()" title="Make hashtag">
        <v-icon>mdi-pound</v-icon>
      </v-btn>
      <v-spacer></v-spacer>

      <div v-for="(ph, index) in placeholders" :key="index">
        <v-select
          class="mx-4 light"
          v-model="placeholderSelection[index]"
          :items="ph.placeholders"
          @blur="placeholderSelection[index] = null"
          @change="insertPlaceholder(placeholderSelection[index])"
          item-value="placeholder"
          :label="ph.label || 'Placeholders'"
          hide-details
          prepend-icon="mdi-code-json"
          single-line
          v-if="ph.placeholders.length"
        >
          <template slot="item" slot-scope="data">
            <template>
              <v-list-item-content>
                <v-list-item-title
                  v-html="data.item.type"
                  v-if="data.item.type"
                ></v-list-item-title>
                <v-list-item-subtitle
                  v-html="data.item.text"
                  v-if="data.item.text"
                ></v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </template>
        </v-select>
      </div>

      <v-btn icon @click="editor.undo()">
        <v-icon>mdi-undo</v-icon>
      </v-btn>
      <v-btn icon @click="editor.redo()">
        <v-icon>mdi-redo</v-icon>
      </v-btn>
    </v-toolbar>
    <v-toolbar dense floating v-if="showLinker">
      <span>URL</span>
      <v-text-field
        class="px-2"
        hide-details
        single-line
        v-model="linkerUrl"
      ></v-text-field>
      <v-btn icon @click="makeLink()">
        <v-icon>mdi-insert</v-icon>
      </v-btn>
    </v-toolbar>
    <v-toolbar floating v-if="showReferenceInsert">
      <div>
        <v-row class="mt-4 mx-2" align="center">
          <v-col class="mr-4">
            <v-checkbox
              label="Hashtag"
              v-model="referenceHashtag"
              inline
            ></v-checkbox>
          </v-col>
          <v-col class="mb-4">
            <v-btn icon @click="insertReference(referenceHashtag)">
              <v-icon>mdi-numeric</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-toolbar>
    <v-alert type="error" v-if="editorError">{{ editorError }}</v-alert>
    <v-sheet
      ref="squire-editor"
      class="squire-editor pa-2"
      :elevation="2"
      :min-height="minHeight"
      :max-height="maxHeight"
    >
      <div></div>
    </v-sheet>

    <v-row class="editor-hint" justify="start">
      <v-expand-transition>
        <div class="mt-1 ml-4 caption" v-if="hint && editorHasFocus">
          {{ hint }}
        </div>
      </v-expand-transition>
    </v-row>
  </section>
</template>

<style lang="scss" scoped>
.editor-wrapper {
  position: relative;

  .editor-hint {
    position: absolute;
  }
}

.squire-editor {
  font-size: 1rem;
  cursor: text;
  overflow-y: auto;

  &:focus {
    outline: none;
    outline-color: transparent;
    border-bottom: 3px solid var(--v-primary-base);
  }
}
</style>